import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PageHeader } from '../ui/PageHeader';
import { StatCard } from '../ui/StatCard';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonCards, SkeletonRows } from '../ui/Skeleton';
import { ExportButton } from '../components/ExportButton';
import { CHART_COLORS } from '../lib/chartTheme';
import { fetchAllRows } from '../lib/fetchAll';
import { timeAgo } from '../lib/timeAgo';
import { supabase } from '../lib/supabase';

/**
 * ═════════════════════════════════════════════════════════════════════════════════════════════
 * AI — the observation deck for Shado's AI search.
 *
 * Evan, 2026-09-15: *"we need an AI tab in the Shado admin dashboard where I can see: people's
 * responses to help Shado improve; how many calls have been used; who's used the AI search feature;
 * what they've asked; what venues have been recalled in the AI; what the prompts and responses have
 * been. We need to be keeping track of all of that so that we can be learning from all of that. I
 * also want to have a recap there of what context it's considering… we want to keep track of how
 * this AI brain is working and make sure that it's really obsessive about documentation."*
 *
 * ── THIS PAGE NEVER WRITES ────────────────────────────────────────────────────────────────────
 * Every query below is a SELECT. There is no mutation anywhere in this file and there must never
 * be one: the tab exists to watch a system whose whole problem is that it has no ground truth, and
 * an observer that edits what it observes is not an observer. Feedback rows in particular are a
 * labelled dataset — marking them "handled" would start destroying the label.
 *
 * ── ⚠️ READ ACCESS IS NOT AUTOMATIC, AND SILENCE WOULD BE THE WORST FAILURE ───────────────────
 * The dashboard runs on the **anon key under RLS** (`src/lib/supabase.ts`) — there is no service
 * role here. Four of the five AI tables had no SELECT policy at all when this page was written, and
 * `ai_answer_feedback` additionally had its table-level SELECT grant revoked. Two different failure
 * shapes come out of that, and they look nothing alike from the client:
 *   · no GRANT   → PostgREST throws `42501 permission denied for table` — a visible error.
 *   · GRANT, no RLS policy → PostgREST returns `[]` with NO error — an empty page that looks like
 *     "there is no data", which is a lie, and the kind of lie a dashboard tells for months.
 * So every source is loaded through `readSource`, which records which of the three states it landed
 * in, and `AccessNotice` prints that per table at the top of the page. An empty table here says
 * "0 rows readable" and names the migration, never a bare "No data".
 * The fix is `supabase/migrations/20260905000000_ai_observability_admin_reads.sql`.
 *
 * ── ⚠️ WHAT THE DATABASE DOES NOT KEEP, AND WHY HALF THIS PAGE IS HONEST ABOUT IT ─────────────
 * `search_concierge_calls` records that a search happened and what it cost. It does **not** record
 * the answer, and it does not record which venues came back. The only two places a RESPONSE is
 * stored anywhere are:
 *   · `ai_answer_feedback.answer` — but only on answers somebody flagged, and
 *   · `search_concierge_answer_cache.payload` — which is a CACHE, not a transcript: written only on
 *     non-personalised turns, keyed by a hash rather than by the query, and read back only within a
 *     6h TTL.
 * So "what venues have been recalled" and "what the responses have been" are answerable today only
 * from a rolling sample plus the complaints. That gap is stated on the surfaces it affects rather
 * than papered over with a number that looks complete. Closing it properly means logging the answer
 * and the returned venue ids per call in `search-concierge` — see the Context tab.
 * ═════════════════════════════════════════════════════════════════════════════════════════════
 */

/* ── Types mirror production columns, verified 2026-09-15 against information_schema ────────── */

type FeedbackRow = {
  id: string;
  created_at: string;
  user_id: string | null;
  /** NULL since 2026-09-21 = the reader sent a score and nothing else. See {@link REASON_SCORE_ONLY}. */
  reason: string | null;
  note: string | null;
  query: string;
  answer: string | null;
  venue_ids: string[] | null;
  viewer_city: string | null;
  signed_in: boolean;
  /**
   * The 1–10 slider on "Help Shado Improve" (app-side `AnswerFeedbackSheet`). NULL for every row
   * written before 2026-09-21, and for a reader who tapped a reason without touching the slider —
   * both are real states and neither is a 1, which is why it is nullable rather than defaulted.
   */
  score: number | null;
};

type CallRow = {
  id: string;
  created_at: string;
  user_id: string | null;
  device_id: string | null;
  query: string | null;
  answer: string | null;
  candidate_count: number | null;
  input_tokens: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  output_tokens: number | null;
  served_from_cache: boolean;
  blocked_reason: string | null;
};

/** `payload` shape written by `search-concierge`: { answer, venues[], followups[] }. */
type AnswerPayload = {
  answer?: string;
  venues?: { name?: string; line?: string }[];
  followups?: { question?: string; preview?: string }[];
};

type AnswerCacheRow = {
  cache_key: string;
  payload: AnswerPayload | null;
  model: string | null;
  created_at: string;
  hit_count: number;
};

type AnonDeviceRow = {
  device_id: string;
  used: number;
  used_day: number;
  day: string;
  first_at: string;
  last_at: string;
};

type IpLimitRow = { ip_hash: string; day: string; minute: number; count: number };

/* ── Source loading + access state ───────────────────────────────────────────────────────────── */

/**
 * `ok` — rows came back. `empty` — the read succeeded and returned nothing, which under RLS is
 * ambiguous (genuinely no rows, OR a missing SELECT policy) and is therefore never rendered as a
 * plain "no data". `blocked` — the read errored, almost always a missing table GRANT.
 */
type SourceState = 'ok' | 'empty' | 'blocked';
type Source<T> = { rows: T[]; state: SourceState; error: string | null };

const EMPTY_SOURCE = { rows: [], state: 'empty' as SourceState, error: null };

async function readSource<T>(
  table: string,
  columns: string,
  orderColumn?: string,
): Promise<Source<T>> {
  try {
    const rows = await fetchAllRows<T>((from, to) => {
      const base = supabase.from(table).select(columns).range(from, to);
      const q = orderColumn ? base.order(orderColumn, { ascending: false }) : base;
      return q as unknown as PromiseLike<{ data: T[] | null; error: { message: string } | null }>;
    });
    return { rows, state: rows.length > 0 ? 'ok' : 'empty', error: null };
  } catch (e: unknown) {
    return { rows: [], state: 'blocked', error: e instanceof Error ? e.message : 'Read failed' };
  }
}

/* ── Constants ───────────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ RATES ARE PRINTED ON SCREEN NEXT TO THE NUMBER THEY PRODUCE, ON PURPOSE. A hardcoded price in
 * a dashboard goes stale silently and then quietly misreports spend forever. Showing the rate makes
 * the estimate checkable by whoever is reading it. Anthropic list price for `claude-sonnet-5` as of
 * 2026-09-15: $2.00 / 1M input, $10.00 / 1M output; a cache READ bills at ~0.1x input and a cache
 * WRITE at ~1.25x input. If the model in `search-concierge` changes, change these with it.
 */
const USD_PER_MTOK = { input: 2.0, output: 10.0, cacheRead: 0.2, cacheWrite: 2.5 } as const;
const MODEL_LABEL = 'claude-sonnet-5';

/** The five reasons the in-app control offers, plus `other` which carries its meaning in `note`. */
/**
 * ⚠️ `reason` IS NULLABLE SINCE 2026-09-21 and a null is a REAL value, not a missing one — Evan:
 * *"if you slide the slider, you should be able to press done with nothing else."* A score with no
 * reason lands with `reason = null`, and without this sentinel it would fall into a blank pill with
 * a blank badge that reads as a rendering bug. Everything downstream keys off a string, so the null
 * is mapped to one here, once, rather than special-cased at each of the four render sites.
 */
export const REASON_SCORE_ONLY = '__score_only__';

const REASON_LABEL: Record<string, string> = {
  [REASON_SCORE_ONLY]: 'Score only',
  wrong_city: 'Wrong city',
  unknown_venues: "Venues they didn't know",
  off_question: "Didn't answer the question",
  too_few: 'Too few places',
  made_up: 'Made something up',
  other: 'Other (typed)',
};

const TABS = [
  { key: 'feedback', label: 'Feedback' },
  { key: 'usage', label: 'Usage' },
  { key: 'people', label: 'Who & what' },
  { key: 'recall', label: 'Venue recall' },
  { key: 'context', label: 'Context' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/* ── Small helpers ───────────────────────────────────────────────────────────────────────────── */

const n = (v: number | null | undefined): number => (typeof v === 'number' ? v : 0);

function dayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function shortDay(key: string): string {
  const [, m, d] = key.split('-');
  return `${Number(m)}/${Number(d)}`;
}

function fullTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
}

function usd(v: number): string {
  if (v === 0) return '$0.00';
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

/** PostgREST `.in()` has a URL-length ceiling — resolve lookups in chunks rather than one huge call. */
async function lookupNames(
  table: string,
  idColumn: string,
  nameColumn: string,
  ids: string[],
): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const unique = Array.from(new Set(ids.filter(Boolean)));
  for (let i = 0; i < unique.length; i += 200) {
    const slice = unique.slice(i, i + 200);
    /* ⚠️ Cast, because supabase-js parses the select string at the TYPE level and cannot parse a
       template literal — the column names are parameters here. The shape is asserted once, below. */
    const { data, error } = (await supabase
      .from(table)
      .select(`${idColumn}, ${nameColumn}`)
      .in(idColumn, slice)) as unknown as {
      data: Record<string, unknown>[] | null;
      error: { message: string } | null;
    };
    if (error) return out; // a failed lookup degrades to raw ids, it never fails the page
    (data ?? []).forEach((row) => {
      const id = row[idColumn];
      const name = row[nameColumn];
      if (typeof id === 'string' && typeof name === 'string' && name.trim()) out.set(id, name);
    });
  }
  return out;
}

/* ── Access notice ───────────────────────────────────────────────────────────────────────────── */

type SourceMeta = { table: string; state: SourceState; error: string | null; count: number; why: string };

/**
 * The most important component on this page. A dashboard that reads nothing and renders a tidy
 * empty state is worse than one that crashes, because nobody investigates it. This prints the
 * actual state of each read, and names the migration that fixes a blocked one.
 */
function AccessNotice({ sources }: { sources: SourceMeta[] }) {
  const blocked = sources.filter((s) => s.state === 'blocked');
  const empty = sources.filter((s) => s.state === 'empty');
  if (blocked.length === 0 && empty.length === 0) return null;

  return (
    <div className="admin-card mb-6 border-warning/30">
      <div className="admin-eyebrow mb-2 text-warning">Read access</div>
      <p className="text-[13px] leading-relaxed text-muted">
        The dashboard reads production with the <span className="text-textPrimary">anon key under RLS</span>, so a
        table with no admin SELECT policy returns nothing — and a table with no GRANT errors outright. Neither is the
        same as "there is no data". Current state of each source:
      </p>
      <ul className="mt-3 space-y-2">
        {sources.map((s) => (
          <li key={s.table} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px]">
            <code className="admin-chip-id">{s.table}</code>
            {s.state === 'ok' ? (
              <span className="admin-status admin-status-success">{s.count.toLocaleString()} rows</span>
            ) : s.state === 'empty' ? (
              <span className="admin-status admin-status-warning">0 rows readable</span>
            ) : (
              <span className="admin-status admin-status-danger">blocked</span>
            )}
            <span className="text-mutedDim">{s.state === 'blocked' ? s.error : s.why}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[12px] leading-relaxed text-mutedDim">
        Fix: apply{' '}
        <code className="text-muted">supabase/migrations/20260905000000_ai_observability_admin_reads.sql</code> — it
        adds an <code className="text-muted">is_admin()</code> SELECT policy plus the matching GRANT to all five
        tables, the same pattern <code className="text-muted">venue_admin_review_queue</code> already uses. Nothing on
        this page writes, so the migration grants SELECT and nothing else.
      </p>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────────────────────────── */

export function AiScreen() {
  const [tab, setTab] = useState<TabKey>('feedback');
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);

  const [feedback, setFeedback] = useState<Source<FeedbackRow>>(EMPTY_SOURCE);
  const [calls, setCalls] = useState<Source<CallRow>>(EMPTY_SOURCE);
  const [answers, setAnswers] = useState<Source<AnswerCacheRow>>(EMPTY_SOURCE);
  const [devices, setDevices] = useState<Source<AnonDeviceRow>>(EMPTY_SOURCE);
  const [ipLimits, setIpLimits] = useState<Source<IpLimitRow>>(EMPTY_SOURCE);
  const [usernames, setUsernames] = useState<Map<string, string>>(new Map());
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());

  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [openUser, setOpenUser] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFatal(null);
    try {
      /* One round of reads for the whole page: every number below then describes the same instant,
         and switching tabs costs nothing. Volumes are small (hundreds of rows) by construction. */
      const [fb, cl, an, dv, ip] = await Promise.all([
        readSource<FeedbackRow>(
          'ai_answer_feedback',
          'id, created_at, user_id, reason, note, query, answer, venue_ids, viewer_city, signed_in, score',
          'created_at',
        ),
        readSource<CallRow>(
          'search_concierge_calls',
          'id, created_at, user_id, device_id, query, answer, candidate_count, input_tokens, cache_read_tokens, cache_write_tokens, output_tokens, served_from_cache, blocked_reason',
          'created_at',
        ),
        readSource<AnswerCacheRow>(
          'search_concierge_answer_cache',
          'cache_key, payload, model, created_at, hit_count',
          'created_at',
        ),
        readSource<AnonDeviceRow>('anon_ai_search_devices', 'device_id, used, used_day, day, first_at, last_at', 'last_at'),
        readSource<IpLimitRow>('search_concierge_ip_limits', 'ip_hash, day, minute, count', 'day'),
      ]);

      setFeedback(fb);
      setCalls(cl);
      setAnswers(an);
      setDevices(dv);
      setIpLimits(ip);

      const userIds = [
        ...cl.rows.map((r) => r.user_id),
        ...fb.rows.map((r) => r.user_id),
      ].filter((x): x is string => Boolean(x));
      const venueIds: string[] = [];
      fb.rows.forEach((r) => (r.venue_ids ?? []).forEach((v) => venueIds.push(v)));

      const [uMap, vMap] = await Promise.all([
        userIds.length ? lookupNames('profiles', 'id', 'username', userIds) : Promise.resolve(new Map<string, string>()),
        /* ⚠️ `venue_name_user_input` is the display-name column on `venues` — there is no `name`.
           Confirmed against production 2026-09-15; `CityOverviews.tsx` reads the same column. */
        venueIds.length
          ? lookupNames('venues', 'id', 'venue_name_user_input', venueIds)
          : Promise.resolve(new Map<string, string>()),
      ]);
      setUsernames(uMap);
      setVenueNames(vMap);
    } catch (e: unknown) {
      setFatal(e instanceof Error ? e.message : 'Failed to load AI data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* ── Derived ──────────────────────────────────────────────────────────────────────────────── */

  const sourceMeta: SourceMeta[] = useMemo(
    () => [
      {
        table: 'ai_answer_feedback',
        state: feedback.state,
        error: feedback.error,
        count: feedback.rows.length,
        why: 'nobody has flagged an answer yet, or the SELECT policy is missing',
      },
      {
        table: 'search_concierge_calls',
        state: calls.state,
        error: calls.error,
        count: calls.rows.length,
        why: 'no searches recorded, or RLS is hiding them (this table has a GRANT but had no policy)',
      },
      {
        table: 'search_concierge_answer_cache',
        state: answers.state,
        error: answers.error,
        count: answers.rows.length,
        why: 'the 6h cache is cold, or the SELECT policy is missing',
      },
      {
        table: 'anon_ai_search_devices',
        state: devices.state,
        error: devices.error,
        count: devices.rows.length,
        why: 'no anonymous searches yet, or the SELECT policy is missing',
      },
      {
        table: 'search_concierge_ip_limits',
        state: ipLimits.state,
        error: ipLimits.error,
        count: ipLimits.rows.length,
        why: 'no per-IP counters yet, or the SELECT policy is missing',
      },
    ],
    [feedback, calls, answers, devices, ipLimits],
  );

  const filteredFeedback = useMemo(
    () =>
      reasonFilter === 'all'
        ? feedback.rows
        : feedback.rows.filter((r) => (r.reason ?? REASON_SCORE_ONLY) === reasonFilter),
    [feedback.rows, reasonFilter],
  );

  const reasonCounts = useMemo(() => {
    const m = new Map<string, number>();
    feedback.rows.forEach((r) => {
      const key = r.reason ?? REASON_SCORE_ONLY;
      m.set(key, (m.get(key) ?? 0) + 1);
    });
    return m;
  }, [feedback.rows]);

  const usage = useMemo(() => {
    const rows = calls.rows;
    const now = Date.now();
    const answered = rows.filter((r) => !r.blocked_reason);
    const since = (days: number) => rows.filter((r) => now - new Date(r.created_at).getTime() < days * 86_400_000);

    const tokens = rows.reduce(
      (acc, r) => ({
        input: acc.input + n(r.input_tokens),
        output: acc.output + n(r.output_tokens),
        cacheRead: acc.cacheRead + n(r.cache_read_tokens),
        cacheWrite: acc.cacheWrite + n(r.cache_write_tokens),
      }),
      { input: 0, output: 0, cacheRead: 0, cacheWrite: 0 },
    );

    const spend =
      (tokens.input / 1_000_000) * USD_PER_MTOK.input +
      (tokens.output / 1_000_000) * USD_PER_MTOK.output +
      (tokens.cacheRead / 1_000_000) * USD_PER_MTOK.cacheRead +
      (tokens.cacheWrite / 1_000_000) * USD_PER_MTOK.cacheWrite;

    /* Daily buckets over the window the data actually covers, capped at 30 days. */
    const byDay = new Map<string, { day: string; signedIn: number; anonymous: number }>();
    rows.forEach((r) => {
      const k = dayKey(r.created_at);
      const e = byDay.get(k) ?? { day: k, signedIn: 0, anonymous: 0 };
      if (r.user_id) e.signedIn += 1;
      else e.anonymous += 1;
      byDay.set(k, e);
    });
    const series = Array.from(byDay.values())
      .sort((a, b) => a.day.localeCompare(b.day))
      .slice(-30)
      .map((d) => ({ ...d, label: shortDay(d.day) }));

    const blockedBy = new Map<string, number>();
    rows.forEach((r) => {
      if (r.blocked_reason) blockedBy.set(r.blocked_reason, (blockedBy.get(r.blocked_reason) ?? 0) + 1);
    });

    return {
      total: rows.length,
      answered: answered.length,
      blocked: rows.length - answered.length,
      anonymous: rows.filter((r) => !r.user_id).length,
      cached: rows.filter((r) => r.served_from_cache).length,
      last7: since(7).length,
      last30: since(30).length,
      distinctUsers: new Set(rows.map((r) => r.user_id).filter(Boolean)).size,
      distinctDevices: new Set(rows.map((r) => r.device_id).filter(Boolean)).size,
      tokens,
      spend,
      series,
      blockedBy: Array.from(blockedBy.entries()).sort((a, b) => b[1] - a[1]),
      /* Live measure of the cached system prefix rather than a number copied out of the edge
         function — `cache_read_tokens` IS the prefix the model re-read on that call. */
      medianCachedPrefix: median(rows.map((r) => n(r.cache_read_tokens)).filter((v) => v > 0)),
    };
  }, [calls.rows]);

  const people = useMemo(() => {
    const m = new Map<
      string,
      { key: string; userId: string | null; deviceId: string | null; calls: CallRow[] }
    >();
    calls.rows.forEach((r) => {
      const key = r.user_id ?? (r.device_id ? `device:${r.device_id}` : 'unattributed');
      const e = m.get(key) ?? {
        key,
        userId: r.user_id,
        deviceId: r.user_id ? null : r.device_id,
        calls: [],
      };
      e.calls.push(r);
      m.set(key, e);
    });
    return Array.from(m.values())
      .map((e) => {
        const times = e.calls.map((c) => new Date(c.created_at).getTime());
        return {
          ...e,
          count: e.calls.length,
          first: new Date(Math.min.apply(null, times)).toISOString(),
          last: new Date(Math.max.apply(null, times)).toISOString(),
          tokens: e.calls.reduce((a, c) => a + n(c.input_tokens) + n(c.output_tokens), 0),
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [calls.rows]);

  /**
   * ⚠️ VENUE RECALL IS ASSEMBLED FROM TWO PARTIAL SOURCES BECAUSE NO COMPLETE ONE EXISTS.
   *   · the answer cache — venue NAMES the model wrote, on non-personalised turns inside the 6h TTL
   *   · feedback rows — venue IDS retrieved for an answer somebody flagged, resolved to names here
   * Counting them together is the best available picture and it is still a sample. The surface says
   * so; do not quote these counts as "how often venue X is recommended".
   */
  const recall = useMemo(() => {
    const m = new Map<string, { name: string; fromAnswers: number; fromFeedback: number }>();
    const bump = (rawName: string, key: 'fromAnswers' | 'fromFeedback') => {
      const name = rawName.trim();
      if (!name) return;
      const e = m.get(name.toLowerCase()) ?? { name, fromAnswers: 0, fromFeedback: 0 };
      e[key] += 1;
      m.set(name.toLowerCase(), e);
    };
    answers.rows.forEach((r) => (r.payload?.venues ?? []).forEach((v) => bump(v?.name ?? '', 'fromAnswers')));
    feedback.rows.forEach((r) =>
      (r.venue_ids ?? []).forEach((id) => bump(venueNames.get(id) ?? '', 'fromFeedback')),
    );
    return Array.from(m.values())
      .map((e) => ({ ...e, total: e.fromAnswers + e.fromFeedback }))
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  }, [answers.rows, feedback.rows, venueNames]);

  const anySourceUsable = feedback.state === 'ok' || calls.state === 'ok' || answers.state === 'ok';

  /* ── Render ───────────────────────────────────────────────────────────────────────────────── */

  return (
    <div className="admin-page max-w-6xl mx-auto">
      <PageHeader
        title="AI"
        subtitle="What Shado's AI search has been asked, what it answered, what it cost, and what people said went wrong."
        actions={
          <button type="button" onClick={() => void load()} className="admin-btn admin-btn-secondary admin-btn-sm">
            Refresh
          </button>
        }
      />

      {fatal ? <ErrorBanner className="mb-4">{fatal}</ErrorBanner> : null}

      <div className="mb-6 flex flex-wrap gap-1.5" role="tablist" aria-label="AI views">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            data-active={tab === t.key}
            onClick={() => setTab(t.key)}
            className="admin-pill"
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <>
          <SkeletonCards cards={4} className="mb-6" />
          <SkeletonRows rows={8} />
        </>
      ) : (
        <>
          <AccessNotice sources={sourceMeta} />

          {tab === 'feedback' ? (
            <FeedbackView
              source={feedback}
              rows={filteredFeedback}
              reasonCounts={reasonCounts}
              reasonFilter={reasonFilter}
              onReasonFilter={setReasonFilter}
              usernames={usernames}
              venueNames={venueNames}
            />
          ) : null}

          {tab === 'usage' ? <UsageView source={calls} usage={usage} /> : null}

          {tab === 'people' ? (
            <PeopleView
              source={calls}
              people={people}
              usernames={usernames}
              devices={devices}
              ipLimits={ipLimits}
              openUser={openUser}
              onToggleUser={(k) => setOpenUser((cur) => (cur === k ? null : k))}
            />
          ) : null}

          {tab === 'recall' ? <RecallView source={answers} recall={recall} answers={answers.rows} /> : null}

          {tab === 'context' ? (
            <ContextView
              medianCachedPrefix={usage.medianCachedPrefix}
              sampleCalls={usage.total}
              anySourceUsable={anySourceUsable}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/* ── Feedback ────────────────────────────────────────────────────────────────────────────────── */

function FeedbackView({
  source,
  rows,
  reasonCounts,
  reasonFilter,
  onReasonFilter,
  usernames,
  venueNames,
}: {
  source: Source<FeedbackRow>;
  rows: FeedbackRow[];
  reasonCounts: Map<string, number>;
  reasonFilter: string;
  onReasonFilter: (r: string) => void;
  usernames: Map<string, string>;
  venueNames: Map<string, string>;
}) {
  if (source.state !== 'ok') {
    return (
      <EmptyState
        title={source.state === 'blocked' ? 'Feedback is not readable' : 'No flagged answers yet'}
        hint={
          source.state === 'blocked'
            ? `${source.error} — apply migration 20260905000000 to grant admins SELECT on ai_answer_feedback.`
            : 'This fills up when someone taps "Help Shado improve" under an AI answer. Zero here can also mean the SELECT policy is missing — check the access notice above.'
        }
      />
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-active={reasonFilter === 'all'}
          onClick={() => onReasonFilter('all')}
          className="admin-pill"
        >
          All <span className="ml-1 tabular-nums text-mutedDim">{source.rows.length}</span>
        </button>
        {Object.keys(REASON_LABEL).map((key) => {
          const count = reasonCounts.get(key) ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={key}
              type="button"
              data-active={reasonFilter === key}
              onClick={() => onReasonFilter(key)}
              className="admin-pill"
            >
              {REASON_LABEL[key]} <span className="ml-1 tabular-nums text-mutedDim">{count}</span>
            </button>
          );
        })}
        <div className="ml-auto">
          <ExportButton
            rows={rows}
            filename="ai-answer-feedback"
            columns={[
              { header: 'created_at', get: (r: FeedbackRow) => r.created_at },
              { header: 'reason', get: (r: FeedbackRow) => r.reason ?? '' },
              { header: 'note', get: (r: FeedbackRow) => r.note ?? '' },
              { header: 'query', get: (r: FeedbackRow) => r.query },
              { header: 'answer', get: (r: FeedbackRow) => r.answer ?? '' },
              { header: 'viewer_city', get: (r: FeedbackRow) => r.viewer_city ?? '' },
              { header: 'signed_in', get: (r: FeedbackRow) => r.signed_in },
              { header: 'score', get: (r: FeedbackRow) => r.score ?? '' },
              { header: 'venue_ids', get: (r: FeedbackRow) => (r.venue_ids ?? []).join(' ') },
            ]}
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="Nothing matches this reason" compact />
      ) : (
        <div className="space-y-4">
          {rows.map((r) => {
            const username = r.user_id ? usernames.get(r.user_id) : null;
            const venues = (r.venue_ids ?? []).map((id) => venueNames.get(id) ?? id.slice(0, 8));
            return (
              <article key={r.id} className="admin-card">
                <div className="mb-3 flex flex-wrap items-center gap-2.5">
                  <span className="admin-badge admin-badge-accent">
                    {REASON_LABEL[r.reason ?? REASON_SCORE_ONLY] ?? r.reason}
                  </span>
                  {/*
                    The 1-10 slider. Rendered only when present: a dash or a "0" on the thousands of
                    rows written before 2026-09-21 would read as a score of zero rather than as an
                    absence, and this column is going to be mostly null for a while.
                  */}
                  {typeof r.score === 'number' ? (
                    <span className="admin-badge" title="How the reader rated this answer, 1-10">
                      {r.score}/10
                    </span>
                  ) : null}
                  {r.viewer_city ? <span className="text-[12px] text-muted">{r.viewer_city}</span> : null}
                  <span className="text-[11px] uppercase tracking-wider text-mutedDim">
                    {r.signed_in ? (username ? `@${username}` : 'signed in') : 'anonymous'}
                  </span>
                  <span className="ml-auto text-[11px] text-mutedDim" title={fullTime(r.created_at)}>
                    {timeAgo(r.created_at)}
                  </span>
                </div>

                {/* ⚠️ An empty `query` is a real state, not a rendering bug — the control snapshots
                    the exchange at open time and a follow-up turn can carry no fresh query text.
                    Saying so beats printing a blank line. */}
                <div className="mb-3">
                  <div className="admin-eyebrow mb-1">They asked</div>
                  <p className="text-[14px] leading-relaxed text-textPrimary">
                    {r.query.trim() ? `“${r.query.trim()}”` : <span className="text-mutedDim">no query captured on this turn</span>}
                  </p>
                </div>

                {r.answer?.trim() ? (
                  <div className="mb-3 rounded-lg border-l-2 border-border bg-surfaceElevated/60 px-3.5 py-2.5">
                    <div className="admin-eyebrow mb-1">Shado answered</div>
                    <p className="text-[13px] leading-relaxed text-muted">{r.answer.trim()}</p>
                  </div>
                ) : null}

                {r.note?.trim() ? (
                  <div className="mb-3">
                    <div className="admin-eyebrow mb-1">They wrote</div>
                    <p className="text-[14px] leading-relaxed text-textPrimary">{r.note.trim()}</p>
                  </div>
                ) : null}

                {venues.length > 0 ? (
                  <div>
                    <div className="admin-eyebrow mb-1.5">Venues retrieved for that answer ({venues.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {venues.map((v, i) => (
                        <span key={`${v}-${i}`} className="admin-badge admin-badge-neutral">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Usage ───────────────────────────────────────────────────────────────────────────────────── */

type Usage = {
  total: number;
  answered: number;
  blocked: number;
  anonymous: number;
  cached: number;
  last7: number;
  last30: number;
  distinctUsers: number;
  distinctDevices: number;
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number };
  spend: number;
  series: { day: string; label: string; signedIn: number; anonymous: number }[];
  blockedBy: [string, number][];
  medianCachedPrefix: number | null;
};

function UsageView({ source, usage }: { source: Source<CallRow>; usage: Usage }) {
  if (source.state !== 'ok') {
    return (
      <EmptyState
        title={source.state === 'blocked' ? 'Call log is not readable' : 'No calls readable'}
        hint={
          source.state === 'blocked'
            ? `${source.error} — apply migration 20260905000000.`
            : 'search_concierge_calls has a table GRANT but had no RLS SELECT policy, so this reads empty even when rows exist. Apply migration 20260905000000 before concluding nobody has searched.'
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <StatCard label="AI searches" value={usage.total} sub="every logged call, blocked ones included" />
        <StatCard label="Last 7 days" value={usage.last7} sub={`${usage.last30.toLocaleString()} in 30 days`} />
        <StatCard
          label="Anonymous"
          value={usage.anonymous}
          sub={usage.total ? `${Math.round((usage.anonymous / usage.total) * 100)}% of calls · ${usage.distinctDevices} devices` : undefined}
        />
        <StatCard label="Signed-in people" value={usage.distinctUsers} sub="distinct accounts that have searched" />
        <StatCard
          label="Blocked"
          value={usage.blocked}
          sub={usage.blockedBy.length ? usage.blockedBy.map(([k, v]) => `${k} ${v}`).join(' · ') : 'none refused'}
        />
        <StatCard
          label="Served from cache"
          value={usage.cached}
          sub="answered without calling the model at all"
        />
      </div>

      <div className="admin-card">
        <div className="admin-eyebrow mb-3">Searches per day — signed in vs anonymous</div>
        {usage.series.length === 0 ? (
          <EmptyState title="No dated calls" compact />
        ) : (
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={usage.series} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={CHART_COLORS.borderStrong} strokeDasharray="2 4" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: CHART_COLORS.mutedDim, fontSize: 10 }}
                  axisLine={{ stroke: CHART_COLORS.borderStrong }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: CHART_COLORS.mutedDim, fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                  contentStyle={{
                    background: CHART_COLORS.surfaceElevated,
                    border: `1px solid ${CHART_COLORS.borderStrong}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: CHART_COLORS.textPrimary }}
                  itemStyle={{ color: CHART_COLORS.mutedDim }}
                />
                <Bar dataKey="signedIn" name="Signed in" stackId="a" fill={CHART_COLORS.accent} radius={[0, 0, 0, 0]} />
                <Bar dataKey="anonymous" name="Anonymous" stackId="a" fill={CHART_COLORS.mutedDim} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-3 flex items-center gap-4 text-[11px] text-mutedDim">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ background: CHART_COLORS.accent }} /> Signed in
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ background: CHART_COLORS.mutedDim }} /> Anonymous
          </span>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-eyebrow mb-3">Tokens and estimated spend</div>
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <StatCard label="Input" value={usage.tokens.input} sub="uncached prompt tokens" />
          <StatCard label="Cache reads" value={usage.tokens.cacheRead} sub="the system prefix, re-read per call" />
          <StatCard label="Cache writes" value={usage.tokens.cacheWrite} sub="prefix written to cache" />
          <StatCard label="Output" value={usage.tokens.output} sub="the answer itself" />
          <StatCard label="Estimated spend" value={usd(usage.spend)} sub="all time, all calls" />
        </div>
        <p className="mt-4 text-[12px] leading-relaxed text-mutedDim">
          Estimated at {MODEL_LABEL} list price — ${USD_PER_MTOK.input.toFixed(2)} in / $
          {USD_PER_MTOK.output.toFixed(2)} out per 1M tokens, cache reads ${USD_PER_MTOK.cacheRead.toFixed(2)} and
          cache writes ${USD_PER_MTOK.cacheWrite.toFixed(2)} per 1M (rates as of 2026-09-15, hardcoded in{' '}
          <code className="text-muted">AiScreen.tsx</code>). It is an estimate from logged token counts, not a bill.
          Blocked and cache-served calls log no tokens, which is why they cost nothing here.
        </p>
      </div>

      <p className="text-[12px] leading-relaxed text-mutedDim">
        ⚠️ A row exists for every call including the refused ones — that is deliberate, so a blocked call is
        counted rather than invisible. "Served from cache" rows answered from{' '}
        <code className="text-muted">search_concierge_answer_cache</code> and never reached the model.
      </p>
    </div>
  );
}

/* ── Who & what ──────────────────────────────────────────────────────────────────────────────── */

type PersonRow = {
  key: string;
  userId: string | null;
  deviceId: string | null;
  calls: CallRow[];
  count: number;
  first: string;
  last: string;
  tokens: number;
};

function PeopleView({
  source,
  people,
  usernames,
  devices,
  ipLimits,
  openUser,
  onToggleUser,
}: {
  source: Source<CallRow>;
  people: PersonRow[];
  usernames: Map<string, string>;
  devices: Source<AnonDeviceRow>;
  ipLimits: Source<IpLimitRow>;
  openUser: string | null;
  onToggleUser: (key: string) => void;
}) {
  // Column-header sorting (Evan 2026-10-07: "need column sort functionality here").
  // Numeric/date columns open descending, Who ascending; clicking the active column flips.
  // Hooks live ABOVE the source-state early return — rules-of-hooks.
  const [peopleSort, setPeopleSort] = useState<'who' | 'count' | 'tokens' | 'first' | 'last'>('count');
  const [peopleSortDir, setPeopleSortDir] = useState<'asc' | 'desc'>('desc');
  const sortBy = (key: 'who' | 'count' | 'tokens' | 'first' | 'last') => {
    setPeopleSort((prev) => {
      if (prev === key) {
        setPeopleSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
        return prev;
      }
      setPeopleSortDir(key === 'who' ? 'asc' : 'desc');
      return key;
    });
  };
  const whoLabel = useCallback(
    (p: PersonRow) =>
      p.userId
        ? (usernames.get(p.userId) ?? p.userId)
        : (p.deviceId ?? 'unattributed'),
    [usernames],
  );
  const sortedPeople = useMemo(() => {
    const dir = peopleSortDir === 'asc' ? 1 : -1;
    const xs = people.slice();
    xs.sort((a, b) => {
      // Sorting by Who keeps anonymous devices at the BOTTOM in both directions (Evan
      // 2026-10-07) — the partition is applied outside the direction flip on purpose.
      if (peopleSort === 'who') {
        const anonOrder = Number(!a.userId) - Number(!b.userId);
        if (anonOrder !== 0) return anonOrder;
        const cmp = whoLabel(a).toLowerCase().localeCompare(whoLabel(b).toLowerCase());
        return (cmp === 0 ? b.count - a.count : cmp) * dir;
      }
      let cmp = 0;
      if (peopleSort === 'count') cmp = a.count - b.count;
      else if (peopleSort === 'tokens') cmp = a.tokens - b.tokens;
      else if (peopleSort === 'first') cmp = a.first.localeCompare(b.first);
      else cmp = a.last.localeCompare(b.last);
      if (cmp === 0) cmp = b.count - a.count; // stable, meaningful tie-break
      return cmp * dir;
    });
    return xs;
  }, [people, peopleSort, peopleSortDir, whoLabel]);

  // Headline strip: signed-in vs anonymous, people and searches, plus daily averages over the
  // full span of the log (first call date → last call date, zero-activity days included — so a
  // quiet week pulls the average down instead of hiding).
  const peopleStats = useMemo(() => {
    let signedPeople = 0, anonPeople = 0, signedSearches = 0, anonSearches = 0;
    const searchersByDay = new Map<string, Set<string>>();
    let searchTotal = 0;
    let minDay: string | null = null;
    let maxDay: string | null = null;
    // Evan's own usage would dominate every number here (148 of ~370 searches), so the strip
    // measures everyone EXCEPT @evan; his row stays in the table below.
    const measured = people.filter((p) => !(p.userId && usernames.get(p.userId) === 'evan'));
    for (const p of measured) {
      if (p.userId) { signedPeople += 1; signedSearches += p.count; }
      else { anonPeople += 1; anonSearches += p.count; }
      for (const c of p.calls) {
        const day = c.created_at.slice(0, 10);
        if (!minDay || day < minDay) minDay = day;
        if (!maxDay || day > maxDay) maxDay = day;
        let set = searchersByDay.get(day);
        if (!set) { set = new Set(); searchersByDay.set(day, set); }
        set.add(p.key);
        searchTotal += 1;
      }
    }
    let spanDays = 1;
    if (minDay && maxDay) {
      spanDays = Math.max(1, Math.round((Date.parse(maxDay) - Date.parse(minDay)) / 86400000) + 1);
    }
    let searcherDaySum = 0;
    searchersByDay.forEach((set) => { searcherDaySum += set.size; });
    return {
      signedPeople, anonPeople, signedSearches, anonSearches,
      avgDailySearches: searchTotal / spanDays,
      avgDailySearchers: searcherDaySum / spanDays,
      spanDays,
    };
  }, [people, usernames]);
  const arrow = (key: 'who' | 'count' | 'tokens' | 'first' | 'last') =>
    peopleSort === key ? (peopleSortDir === 'desc' ? ' ▼' : ' ▲') : '';
  // Master expand (Evan 2026-10-07: "when i toggle that it shows the questions for all").
  // While it is on, every row is open EXCEPT ones individually hidden ("i should still be able
  // to collapse individual users"). Flipping the master on clears those per-row overrides.
  const [allOpen, setAllOpen] = useState(false);
  const [closedWhileAll, setClosedWhileAll] = useState<Set<string>>(new Set());

  if (source.state !== 'ok') {
    return (
      <EmptyState
        title={source.state === 'blocked' ? 'Call log is not readable' : 'No calls readable'}
        hint={
          source.state === 'blocked'
            ? `${source.error} — apply migration 20260905000000.`
            : 'Apply migration 20260905000000; this table has a GRANT but no RLS SELECT policy, so it reads empty even when rows exist.'
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="admin-card">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="admin-eyebrow">Who has used AI search</div>
          <ExportButton
            rows={people}
            filename="ai-search-by-person"
            columns={[
              { header: 'who', get: (r: PersonRow) => (r.userId ? usernames.get(r.userId) ?? r.userId : r.deviceId ?? 'unattributed') },
              { header: 'signed_in', get: (r: PersonRow) => Boolean(r.userId) },
              { header: 'searches', get: (r: PersonRow) => r.count },
              { header: 'first', get: (r: PersonRow) => r.first },
              { header: 'last', get: (r: PersonRow) => r.last },
              { header: 'queries', get: (r: PersonRow) => r.calls.map((c) => c.query ?? '').join(' | ') },
            ]}
          />
        </div>
        <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-mutedDim">
          <span>
            Searchers: <span className="font-medium text-white">{peopleStats.signedPeople}</span> signed in ·{' '}
            <span className="font-medium text-white">{peopleStats.anonPeople}</span> anonymous
          </span>
          <span>
            Searches: <span className="font-medium text-white">{peopleStats.signedSearches.toLocaleString()}</span> signed in ·{' '}
            <span className="font-medium text-white">{peopleStats.anonSearches.toLocaleString()}</span> anonymous
          </span>
          <span title={`Over the full ${peopleStats.spanDays}-day span of the log, quiet days included`}>
            Daily average: <span className="font-medium text-white">{peopleStats.avgDailySearchers.toFixed(1)}</span> searchers ·{' '}
            <span className="font-medium text-white">{peopleStats.avgDailySearches.toFixed(1)}</span> searches
          </span>
          <span className="text-mutedDim/70">excludes @evan</span>
        </div>
        <div className="overflow-x-auto">
          <table className="admin-table admin-table--compact">
            <thead>
              <tr>
                <th>
                  <button type="button" className="cursor-pointer hover:text-white" onClick={() => sortBy('who')}>
                    Who{arrow('who')}
                  </button>
                </th>
                <th className="num">
                  <button type="button" className="cursor-pointer hover:text-white" onClick={() => sortBy('count')}>
                    Searches{arrow('count')}
                  </button>
                </th>
                <th className="num">
                  <button type="button" className="cursor-pointer hover:text-white" onClick={() => sortBy('tokens')}>
                    Tokens{arrow('tokens')}
                  </button>
                </th>
                <th>
                  <button type="button" className="cursor-pointer hover:text-white" onClick={() => sortBy('first')}>
                    First{arrow('first')}
                  </button>
                </th>
                <th>
                  <button type="button" className="cursor-pointer hover:text-white" onClick={() => sortBy('last')}>
                    Last{arrow('last')}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="cursor-pointer whitespace-nowrap hover:text-white"
                    onClick={() => {
                      setClosedWhileAll(new Set());
                      setAllOpen((v) => !v);
                    }}
                  >
                    {allOpen ? 'Hide all' : 'See details (all)'}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPeople.map((p) => {
                const open = allOpen ? !closedWhileAll.has(p.key) : openUser === p.key;
                const who = p.userId
                  ? usernames.get(p.userId)
                    ? `@${usernames.get(p.userId)}`
                    : p.userId.slice(0, 8)
                  : p.deviceId
                    ? `device ${p.deviceId.slice(0, 14)}`
                    : 'unattributed';
                return (
                  <React.Fragment key={p.key}>
                    <tr data-selected={open}>
                      <td className="key">
                        {who}
                        {!p.userId ? (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-mutedDim">anon</span>
                        ) : null}
                      </td>
                      <td className="num">{p.count}</td>
                      <td className="num">{p.tokens.toLocaleString()}</td>
                      <td className="text-mutedDim">{fullTime(p.first)}</td>
                      <td className="text-mutedDim">{timeAgo(p.last)}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          onClick={() => {
                            if (allOpen) {
                              // Master mode: toggle just this row via the per-row override.
                              setClosedWhileAll((prev) => {
                                const next = new Set(prev);
                                if (next.has(p.key)) next.delete(p.key);
                                else next.add(p.key);
                                return next;
                              });
                            } else {
                              onToggleUser(p.key);
                            }
                          }}
                        >
                          {open ? 'Hide' : 'See details'}
                        </button>
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td colSpan={6}>
                          {/* The FULL transcript, oldest first (Evan 2026-10-21: "i want to see
                              the full transcripts of peoples convos with the AI"). The query is
                              their turn, the stored answer is the AI's — verbatim. */}
                          <div className="space-y-3 py-1">
                            {p.calls
                              .slice()
                              .sort((a, b) => a.created_at.localeCompare(b.created_at))
                              .map((c) => (
                                <div key={c.id} className="space-y-1">
                                  <div className="flex flex-wrap items-baseline gap-2 text-[12px]">
                                    <span className="text-mutedDim tabular-nums">{fullTime(c.created_at)}</span>
                                    <span className="font-medium text-textPrimary">
                                      {c.query?.trim() ? `“${c.query.trim()}”` : <span className="text-mutedDim">no query text</span>}
                                    </span>
                                    {c.blocked_reason ? (
                                      <span className="admin-badge admin-badge-danger">{c.blocked_reason}</span>
                                    ) : null}
                                    {c.served_from_cache ? (
                                      <span className="admin-badge admin-badge-neutral">cached</span>
                                    ) : null}
                                    {c.candidate_count != null ? (
                                      <span className="text-mutedDim">{c.candidate_count} candidates</span>
                                    ) : null}
                                  </div>
                                  {c.answer?.trim() ? (
                                    <div className="whitespace-pre-wrap rounded-lg border border-border/60 bg-canvas/60 px-3 py-2 text-[12px] leading-relaxed text-muted">
                                      {c.answer.trim()}
                                    </div>
                                  ) : (
                                    <div className="text-[11px] italic text-mutedDim">
                                      {c.blocked_reason ? 'blocked — no answer produced' : 'no answer stored for this call'}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[12px] leading-relaxed text-mutedDim">
          ⚠️ On a follow-up the logged <code className="text-muted">query</code> is the RETRIEVAL query, which carries
          the original question plus the follow-up joined together — that is why some read like two questions at once.
          The model is asked only the latest turn; the thread above it is sent as real prior messages.
        </p>
      </div>

      <div className="admin-card">
        <div className="admin-eyebrow mb-3">Anonymous devices — three searches a day</div>
        {devices.state !== 'ok' ? (
          <EmptyState
            title={devices.state === 'blocked' ? 'Not readable' : 'No anonymous devices recorded'}
            hint={devices.state === 'blocked' ? devices.error ?? undefined : 'Anonymous AI search is capped at 3/day per device, 3/min, 90 lifetime — plus a per-IP layer.'}
            compact
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table admin-table--compact">
              <thead>
                <tr>
                  <th>Device</th>
                  <th className="num">Today</th>
                  <th className="num">Lifetime</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {devices.rows.map((d) => (
                  <tr key={d.device_id}>
                    <td className="key">{d.device_id.slice(0, 22)}</td>
                    <td className="num">
                      {d.used_day}
                      {d.used_day >= 3 ? <span className="ml-1.5 text-warning">capped</span> : null}
                    </td>
                    <td className="num">{d.used}</td>
                    <td className="text-mutedDim">{fullTime(d.first_at)}</td>
                    <td className="text-mutedDim">{timeAgo(d.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[12px] leading-relaxed text-mutedDim">
          ⚠️ A device id is <span className="text-muted">self-reported and forgeable</span> — it groups requests, it
          does not identify anyone. The per-IP counters below are the only layer a forged id cannot escape.
          {ipLimits.state === 'ok'
            ? ` ${ipLimits.rows.length.toLocaleString()} IP-day counters recorded.`
            : ipLimits.state === 'blocked'
              ? ' Per-IP counters are not readable yet.'
              : ' No per-IP counters recorded yet.'}
        </p>
      </div>
    </div>
  );
}

/* ── Venue recall + recent answers ───────────────────────────────────────────────────────────── */

function RecallView({
  source,
  recall,
  answers,
}: {
  source: Source<AnswerCacheRow>;
  recall: { name: string; fromAnswers: number; fromFeedback: number; total: number }[];
  answers: AnswerCacheRow[];
}) {
  return (
    <div className="space-y-6">
      <div className="admin-card border-warning/25">
        <div className="admin-eyebrow mb-2 text-warning">Read this before trusting the counts</div>
        <p className="text-[13px] leading-relaxed text-muted">
          <span className="text-textPrimary">Nothing logs which venues an answer returned.</span>{' '}
          <code className="text-muted">search_concierge_calls</code> stores the question, the candidate count and the
          token counts — not the answer and not the venue list. So recall is assembled from the only two places a
          response survives: the <span className="text-textPrimary">6-hour answer cache</span> (written only on
          non-personalised turns, so it is a rolling sample, not a transcript) and the{' '}
          <span className="text-textPrimary">venues attached to flagged answers</span>. Treat the numbers as a sample.
          To make this complete, <code className="text-muted">search-concierge</code> would need to log the answer text
          and the returned venue ids per call.
        </p>
      </div>

      <div className="admin-card">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="admin-eyebrow">Venues the AI has named</div>
          <ExportButton
            rows={recall}
            filename="ai-venue-recall"
            columns={[
              { header: 'venue', get: (r: { name: string }) => r.name },
              { header: 'in_cached_answers', get: (r: { fromAnswers: number }) => r.fromAnswers },
              { header: 'in_flagged_answers', get: (r: { fromFeedback: number }) => r.fromFeedback },
              { header: 'total', get: (r: { total: number }) => r.total },
            ]}
          />
        </div>
        {recall.length === 0 ? (
          <EmptyState
            title={source.state === 'blocked' ? 'Answer cache is not readable' : 'No recalled venues yet'}
            hint={
              source.state === 'blocked'
                ? `${source.error} — apply migration 20260905000000.`
                : 'The answer cache is empty or cold (6h TTL) and no feedback row carries venue ids.'
            }
            compact
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table admin-table--compact">
              <thead>
                <tr>
                  <th className="rank">#</th>
                  <th>Venue</th>
                  <th className="num">In cached answers</th>
                  <th className="num">In flagged answers</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {recall.map((r, i) => (
                  <tr key={r.name}>
                    <td className="rank">{i + 1}</td>
                    <td className="key">{r.name}</td>
                    <td className="num">{r.fromAnswers || '—'}</td>
                    <td className="num">{r.fromFeedback || '—'}</td>
                    <td className="num">{r.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-card">
        <div className="admin-eyebrow mb-3">Recent answers, verbatim ({answers.length})</div>
        {answers.length === 0 ? (
          <EmptyState title="No cached answers" hint="The cache holds six hours of non-personalised answers." compact />
        ) : (
          <div className="space-y-4">
            {answers.map((a) => (
              <div key={a.cache_key} className="rounded-lg border border-border bg-surfaceElevated/50 px-4 py-3">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-mutedDim">
                  <span title={fullTime(a.created_at)}>{timeAgo(a.created_at)}</span>
                  {a.model ? <span className="admin-badge admin-badge-neutral">{a.model}</span> : null}
                  <span>{a.hit_count} replay{a.hit_count === 1 ? '' : 's'}</span>
                  {/* The row is keyed by a hash of model + schema version + query + scope + thread,
                      so the question itself is NOT recoverable from the cache. Say so rather than
                      leaving a reader hunting for a query column that does not exist. */}
                  <span className="text-mutedDim/70">question not stored — row is keyed by hash</span>
                </div>
                <p className="text-[13px] leading-relaxed text-textPrimary">{a.payload?.answer ?? '—'}</p>
                {(a.payload?.venues ?? []).length > 0 ? (
                  <ul className="mt-2.5 space-y-1">
                    {(a.payload?.venues ?? []).map((v, i) => (
                      <li key={`${v?.name ?? i}`} className="text-[12px] text-muted">
                        <span className="text-textPrimary">{v?.name ?? '—'}</span>
                        {v?.line ? <span className="text-mutedDim"> — {v.line}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {(a.payload?.followups ?? []).length > 0 ? (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {(a.payload?.followups ?? []).map((f, i) => (
                      <span key={`${f?.question ?? i}`} className="admin-badge admin-badge-neutral">
                        {f?.question ?? ''}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Context recap ───────────────────────────────────────────────────────────────────────────── */

type ContextBlock = { title: string; body: React.ReactNode; source?: string };

/**
 * ⚠️ THIS IS A RECAP, NOT A COPY OF THE PROMPT, AND THAT IS DELIBERATE. Pasting the system prompt in
 * here would create a second copy that drifts the first time somebody edits the edge function, and a
 * stale copy of a prompt is worse than no copy — it is read as truth. What is written below is the
 * SHAPE of the context: which inputs exist, what each contributes, and what is deliberately withheld.
 * Every block names the file that is authoritative. The one live number on this tab (the cached
 * prefix size) is measured from `cache_read_tokens` in the call log rather than typed in.
 */
function ContextView({
  medianCachedPrefix,
  sampleCalls,
  anySourceUsable,
}: {
  medianCachedPrefix: number | null;
  sampleCalls: number;
  anySourceUsable: boolean;
}) {
  const blocks: ContextBlock[] = [
    {
      title: 'The call',
      source: 'supabase/functions/search-concierge/index.ts',
      body: (
        <>
          One search is one request to <code className="text-muted">{MODEL_LABEL}</code>. It carries a large{' '}
          <span className="text-textPrimary">cached system prefix</span> (the rules), then a per-search{' '}
          <span className="text-textPrimary">volatile user turn</span> (this question and these venues), with any
          prior turns of the thread sent as real user/assistant messages above it — which is what makes a follow-up a
          follow-up rather than a longer question.
        </>
      ),
    },
    {
      title: 'The cached prefix — the rules',
      source: 'SYSTEM in search-concierge/index.ts',
      body: (
        <>
          {medianCachedPrefix != null ? (
            <>
              Measured live at{' '}
              <span className="text-textPrimary">{medianCachedPrefix.toLocaleString()} tokens</span> (median{' '}
              <code className="text-muted">cache_read_tokens</code> across {sampleCalls.toLocaleString()} logged
              calls), so it is re-read on every search and billed at the cache-read rate rather than full price.{' '}
            </>
          ) : anySourceUsable ? (
            <>No token counts logged yet, so the prefix size cannot be measured from data. </>
          ) : (
            <>Cannot be measured until the call log is readable. </>
          )}
          It holds: what the model may say (only the venues in the turn, never an invented price, hour or tag), the
          rule that a venue name is never shortened (a shortened name matches no row, so the card silently fails to
          render), the voice, the hard ceiling of two sentences / 25 words, how many places to name, how to treat
          rater quotes, and what it may say about friends and mutuals.
        </>
      ),
    },
    {
      title: 'What the turn says about this search',
      source: 'userTurn in search-concierge/index.ts',
      body: (
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <code className="text-muted">Asked:</code> the latest question only — on a follow-up this differs from the
            retrieval query on purpose.
          </li>
          <li>
            <code className="text-muted">Scope:</code> present only when the question is global or regional, naming the
            cities searched, so a worldwide ranking is never described as a local one.
          </li>
          <li>
            <code className="text-muted">Around:</code> the first row's city — suppressed on global and regional lists,
            where it would be a lie.
          </li>
          <li>How many venues the list holds versus how many the model can see, so "more options" can be honest.</li>
          <li>The follow-up budget, and the venue cap when the question asked for a number.</li>
          <li>On a widened search, a line saying which rows matched every word and which matched only part.</li>
          <li>People on Shado, identity only — present only when the question was about a person.</li>
        </ul>
      ),
    },
    {
      title: 'What each venue row carries',
      source: 'renderCandidate in search-concierge/index.ts',
      body: (
        <ul className="ml-4 list-disc space-y-1">
          <li>Name, venue type (bar or club), city, price, Shado score with rater count, distance.</li>
          <li>Criteria scores to one decimal — the same numbers the venue page prints, so the answer can quote one.</li>
          <li>Up to three consensus tags per dimension, ordered by how many distinct raters chose them.</li>
          <li>The stored description, truncated to 240 characters.</li>
          <li>Up to six rater quotes, inside an explicit fence because they are other people's sentences, not ours.</li>
          <li>A friends line (count and average) when the viewer's friends have rated it — and nothing at all when they haven't.</li>
          <li>A mutuals line instead, only on rows where the friends line is silent.</li>
          <li>An unnamed affinity hint on up to three rows: a ranking prior the model may use and may never state.</li>
        </ul>
      ),
    },
    {
      title: 'What is deliberately withheld',
      source: 'search-concierge/index.ts',
      body: (
        <ul className="ml-4 list-disc space-y-1">
          <li>
            <span className="text-textPrimary">Foursquare's category.</span> Better coverage than Shado's own venue
            type, and it bought that coverage with errors the model stated as fact. The client still uses it; the model
            no longer sees it.
          </li>
          <li>
            <span className="text-textPrimary">Which rows this person has already been shown.</span> Marking them made
            the model narrate the bookkeeping. The seen set now only filters rows server-side, on a turn that
            explicitly asks for the unfamiliar.
          </li>
          <li>
            <span className="text-textPrimary">The venue name behind an affinity hint</span> — a named venue in the
            prompt became a similarity claim in the answer three separate times.
          </li>
          <li>
            <span className="text-textPrimary">A zero friends count.</span> Sending "friends: 0" invites a remark about
            the absence; sending nothing gives it nothing to remark on.
          </li>
        </ul>
      ),
    },
    {
      title: 'Retrieval',
      source: 'search_venue_candidates + search-concierge/index.ts',
      body: (
        <>
          The RPC returns <span className="text-textPrimary">10 venues</span>; the client renders all ten as search
          results, while the model is shown the top <span className="text-textPrimary">8</span> (all ten when the
          question asked for a number). The answer names at most{' '}
          <span className="text-textPrimary">3</span> venues, up to 10 when a number was requested. Retrieval falls
          back to the caller's surroundings when it cannot place the question, which is exactly why every row carries
          its city — it is the only evidence the model has that retrieval missed.
        </>
      ),
    },
    {
      title: 'Caps, and where they are enforced',
      source: 'check_ai_search_rate_limit, consume_anon_ai_search',
      body: (
        <>
          Verified against production 2026-09-15. Signed in:{' '}
          <span className="text-textPrimary">5/min, 40/day</span>. Per IP:{' '}
          <span className="text-textPrimary">15/min, 150/day</span>. Global circuit breaker:{' '}
          <span className="text-textPrimary">750/day</span>. Anonymous:{' '}
          <span className="text-textPrimary">3/day, 3/min, 90 lifetime</span> per device. Every one is enforced in
          Postgres, not in the client — a client-side counter is reset by deleting the app.
        </>
      ),
    },
    {
      title: '⚠️ What is NOT kept, and what it costs us',
      source: 'search_concierge_calls',
      body: (
        <>
          <span className="text-textPrimary">The answer is not logged per call, and neither are the venues it named.</span>{' '}
          The call log holds the question, the candidate count and the token counts. So today, "what did the AI say"
          is answerable only from the 6-hour cache (non-personalised turns only) and from answers somebody flagged —
          i.e. from a sample and from the complaints. Every question on this page about quality rather than volume is
          limited by that. The fix is two extra columns written at the same moment the call row is written.
        </>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[13px] leading-relaxed text-muted">
        A recap of what the AI is actually considering when it answers — written here so the brain can be reasoned
        about without reading 5,400 lines of edge function. It describes the shape of the context, never a copy of the
        prompt: a second copy would drift the first time the function changes, and a stale copy reads as truth. Each
        block names the file that is authoritative.
      </p>
      {blocks.map((b) => (
        <section key={b.title} className="admin-card">
          <h3 className="mb-1.5 text-[14px] font-semibold text-textPrimary">{b.title}</h3>
          {b.source ? <div className="admin-eyebrow mb-2.5">{b.source}</div> : null}
          <div className="text-[13px] leading-relaxed text-muted">{b.body}</div>
        </section>
      ))}
    </div>
  );
}
